import { useLayoutEffect, useState, useMemo, useCallback, useRef, useEffect, memo } from "react"
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import useAppStore from "../../store/useAppStore";
import useLayoutStore from "../../store/useLayoutStore";
import { buildMonthCalendarDays } from "../../utils/monthCalendarGrid";

import LoadingSpinner from "../elements/LoadingSpinner";
import AbsenceModal, { AbsenceOverlapModal } from "../elements/AbsenceModal";
import CalendarItemDetailCard from "../elements/CalendarItemDetailCard";

const ALL_GROUPS_VALUE = 'Sve grupe';
const NO_GROUP_LABEL = 'Bez grupe';

const translateGroupDisplay = (name, t) => {
    if (name === ALL_GROUPS_VALUE) return t("filters.allGroups");
    if (name === NO_GROUP_LABEL) return t("filters.noGroup");
    return name;
};

const CalendarView = () => {
    const { t } = useTranslation("calendar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);

    const activeThemeClass = isDarkTheme ? 'dark-color' : 'white-color';

    const calendarData = useAppStore(state => state.calendarData);
    const setCalendarData = useAppStore(state => state.setCalendarData);
    const absenceRequestSuccessToast = useAppStore(state => state.absenceRequestSuccessToast);

    const [selectedGroup, setSelectedGroup] = useState(ALL_GROUPS_VALUE);

    useLayoutEffect(() => {
        if (!calendarData) {
            setCalendarData(new Date().getMonth(), new Date().getFullYear())
            // setCalendarData(11, 2025)
        }
    }, [])

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>{t("title")}</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>{t("description")}</div>
                    <CalendarHeaderButtonsView />
                </div>
            </div>
            <AnimatePresence>
                {absenceRequestSuccessToast && <AbsenceRequestSuccessToast key="absence-toast" toastType={absenceRequestSuccessToast} />}
            </AnimatePresence>
            <AnimatePresence mode="wait">
                {calendarData === null ? (
                    <LoadingSpinner />
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="cv-content-container">
                            <div className="cv-content-div">
                                <ViewCalendarToolbar selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} />
                                <ViewCalendarGrid selectedGroup={selectedGroup} />
                                <CalendarItemDetailCard />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const ViewCalendarGrid = ({ selectedGroup }) => {
    const { t } = useTranslation("calendar");
    const weekdays = t("weekdays", { returnObjects: true });
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);

    const calendarFilter = useAppStore(state => state.calendarFilter);
    const calendarData = useAppStore(state => state.calendarData);
    const loadingCalendarData = useAppStore(state => state.loadingCalendarData);

    // Visina reda i korak za redove sa vise zahteva dolaze iz CSS-a
    // (--cv-row-h i --cv-row-step u calendarView.css), da bi se skalirali po ekranu.
    const { calendarDays, numberOfRows } = useMemo(() => {
        const { firstDisplayedDate, lastDisplayedDate, month, year } = calendarFilter;
        if (firstDisplayedDate == null || lastDisplayedDate == null) {
            return { calendarDays: [], numberOfRows: 0 };
        }
        return buildMonthCalendarDays(year, month);
    }, [calendarFilter.firstDisplayedDate, calendarFilter.lastDisplayedDate, calendarFilter.month, calendarFilter.year]);

    const calendarDataItems = useMemo(() => {
        if (!calendarData) return [];
        const employeesData = useAppStore.getState().employeesData;
        const items = [];

        const getIndex = (row, date) => {
            for (let i = row * 7; i < row * 7 + 7; i++) {
                if (calendarDays[i].date === date) {
                    return i % 7 + 1;
                }
            }
        };

        for (let row = 0; row < numberOfRows; row++) {
            let index = 0;

            const first = calendarDays[row * 7];
            const last = calendarDays[row * 7 + 6];

            const rowStart = new Date(first.year, first.month, first.date);
            const rowEnd = new Date(last.year, last.month, last.date);

            calendarData.forEach(item => {
                const startItem = new Date(item.godina_pocetak, item.mesec_pocetak - 1, item.dan_pocetak);
                const noEnd = item.godina_kraj == null;
                const endItem = noEnd ? null : new Date(item.godina_kraj, item.mesec_kraj - 1, item.dan_kraj);

                if (!noEnd && (endItem < rowStart || startItem > rowEnd)) return;
                if (noEnd && startItem > rowEnd) return;

                const startCol = startItem <= rowStart ? 1 : getIndex(row, startItem.getDate());
                const endCol = noEnd ? 7 : (endItem >= rowEnd ? 7 : getIndex(row, endItem.getDate()));

                const grupa = employeesData?.[item.user_id]?.group ?? 'Bez grupe';

                items.push({
                    gridRow: row + 1,
                    start: startCol,
                    end: endCol,
                    index: index++,
                    type: item.type,
                    user_id: item.user_id,
                    realStart: startItem >= rowStart,
                    realEnd: noEnd ? false : endItem <= rowEnd,
                    zahtev_id: item._id,
                    potvrda: item.potvrda,
                    odbijen: item.odbijen,
                    grupa,
                });
            });
        }

        return items;
    }, [calendarData, calendarDays, numberOfRows]);

    const filteredCalendarDataItems = useMemo(() => {
        if (selectedGroup === ALL_GROUPS_VALUE) {
            return calendarDataItems;
        }
        return calendarDataItems.filter(item => {
            const itemGroupDisplayName = displayGroupName(item.grupa);
            return itemGroupDisplayName === selectedGroup;
        });
    }, [calendarDataItems, selectedGroup]);

    const rowExtraSteps = useMemo(() => {
        const steps = Array(numberOfRows).fill(0);

        calendarDataItems.forEach(item => {
            if (item.index > 3) {
                const row = item.gridRow - 1;
                steps[row] = Math.max(steps[row], item.index - 4);
            }
        });

        return steps;
    }, [calendarData]);

    const gridRows = rowExtraSteps
        .map(steps => steps === 0
            ? 'var(--cv-row-h)'
            : `calc(var(--cv-row-h) + ${steps} * var(--cv-row-step))`)
        .join(' ');
    const gridKey = `${calendarFilter.year}-${calendarFilter.month}`;


    return (
        <AnimatePresence mode="wait">
            {loadingCalendarData ? (
                <LoadingSpinner />
            ) : (
                <motion.div
                    key={gridKey}
                    className="cv-grid-container"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.19, ease: "easeOut" }}
                >
                    <div
                        className={`cv-calendar-grid ${isDarkTheme ? 'dark-calendar-grid' : ''}`}
                        style={{ gridTemplateRows: `auto ${gridRows}` }}
                    >
                        {/* <!-- header: dani u nedelji --> */}
                        {weekdays.map((dayName) => (
                            <div key={dayName} className="cv-day-name">{dayName}</div>
                        ))}

                        {/* <!-- 5 x 7 = 35 polja --> */}
                        {calendarDays.map((day, index) => (
                            <div
                                key={index}
                                className={`cv-day ${!day.isCurrentMonth ? 'cv-day-other-month' : ''}`}
                            >
                                {day.date}
                            </div>
                        ))}
                    </div>
                    <div
                        className="cv-calendar-overlay"
                        style={{ gridTemplateRows: gridRows }}
                    >
                        <AnimatePresence>
                            {filteredCalendarDataItems.map((item) => (
                                <MemoizedCalendarDataItem key={`${item.zahtev_id}-${item.gridRow}-${item.start}-${item.end}-${item.index}`} data={item} />
                            ))}
                        </AnimatePresence>
                    </div>

                </motion.div>
            )}
        </AnimatePresence>
    )
}

const CalendarDataItem = ({ data }) => {
    const { t } = useTranslation("calendar");
    // Koristimo selektore koji vraćaju samo boolean za ovu komponentu
    // Tako će se komponenta re-renderovati samo kada se promeni relevantan ID
    const isSelected = useAppStore(state => state.selectedCalendarRequestId === data.zahtev_id);
    const isHovered = useAppStore(state => state.hoveredCalendarRequestId === data.zahtev_id);
    const setSelectedCalendarRequestId = useAppStore(state => state.setSelectedCalendarRequestId);
    const setHoveredCalendarRequestId = useAppStore(state => state.setHoveredCalendarRequestId);

    const { className, employeeData } = useMemo(() => {
        let typeClass = "cv-data-item-type-sluzbeni-put";
        let statusClass = data.odbijen ? "cv-data-item-status-odbijen" : (data.potvrda ? "" : "cv-data-item-status-nepotvrdjen");
        let typeText = "Zahtev na čekanju";

        if (data.type === "bolovanje") {
            typeClass = "cv-data-item-type-bolovanje";
            typeText = "Bolovanje";
        } else if (data.type === "godisnji_odmor") {
            typeClass = "cv-data-item-type-godisnji-odmor";
            typeText = "Godišnji odmor";
        } else {
            typeText = "Službeni put";
        }

        if (data.odbijen) {
            typeText = "Zahtev odbijen";
        } else if (!data.potvrda) {
            typeText = "Zahtev na čekanju";
        }

        let triangleClass = "";
        if (!data.realEnd && !data.realStart) {
            triangleClass = "cv-triangle-both";
        } else if (!data.realEnd) {
            triangleClass = "cv-triangle-right";
        } else if (!data.realStart) {
            triangleClass = "cv-triangle-left";
        }

        const employeeData = useAppStore.getState().employeesData[data.user_id];

        return {
            className: `cv-data-item ${typeClass} ${statusClass} ${triangleClass}`,
            employeeData: employeeData,
            typeText: typeText,
        };
    }, [data.odbijen, data.potvrda, data.type, data.user_id, data.realEnd, data.realStart]);

    if (!employeeData) return null;

    const finalClassName = `${className}${isSelected || isHovered ? ' cv-data-item-hovered' : ''}`;

    const handleClick = () => setSelectedCalendarRequestId(data.zahtev_id);
    const handleMouseEnter = () => setHoveredCalendarRequestId(data.zahtev_id);
    const handleMouseLeave = () => setHoveredCalendarRequestId(null);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.4 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
                gridRow: data.gridRow,
                gridColumnStart: data.start,
                gridColumnEnd: data.end + 1,
            }}
        >
            <div
                className={finalClassName}
                style={{
                    transform: `translateY(calc(${data.index} * var(--cv-item-step)))${!data.realStart ? ' translateX(-2px)' : ''}`,
                }}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
                aria-label={t("actions.showDetailsAria", { id: data.zahtev_id })}
            >
                {employeeData.ime} {employeeData.prezime}
            </div>
        </motion.div>
    )
}

const MemoizedCalendarDataItem = memo(CalendarDataItem, (prevProps, nextProps) => prevProps.data === nextProps.data);

const ViewCalendarToolbar = ({ selectedGroup, setSelectedGroup }) => {
    const { t } = useTranslation("calendar");
    const months = t("months", { returnObjects: true });

    const calendarFilter = useAppStore(state => state.calendarFilter);
    const setCalendarData = useAppStore(state => state.setCalendarData);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);

    const handleMonthChange = (month) => {
        setCalendarData(month, calendarFilter.year);
        setIsMonthOpen(false);
    }

    const handleYearChange = (year) => {
        setCalendarData(calendarFilter.month, year);
        setIsYearOpen(false);
    }

    const [isMonthOpen, setIsMonthOpen] = useState(false);
    const [isYearOpen, setIsYearOpen] = useState(false);

    return (
        <div className="cv-toolbar-container">
            <div className="cv-filters">
                {/* Mesec */}
                <div className="cv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>{t("filters.month")}</label>
                    <div
                        className={`cv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsMonthOpen(!isMonthOpen)}
                    >
                        {months[calendarFilter.month]}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isMonthOpen && (
                                <motion.div
                                    className={`cv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {months.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className={`cv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                            onClick={() => handleMonthChange(idx)}
                                        >
                                            {m}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Godina */}
                <div className="cv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>{t("filters.year")}</label>
                    <div
                        className={`cv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsYearOpen(!isYearOpen)}
                    >
                        {calendarFilter.year}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isYearOpen && (
                                <motion.div
                                    className={`cv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {Array.from({ length: 6 }, (_, i) => {
                                        const currentYear = new Date().getFullYear();
                                        // Prikazujemo narednu godinu (i=0), trenutnu (i=1), i 4 godine unazad (i=2-5)
                                        const y = i === 0 ? currentYear + 1 : currentYear - (i - 1);
                                        return (
                                            <div
                                                key={y}
                                                className={`cv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                                onClick={() => handleYearChange(y)}
                                            >
                                                {y}
                                            </div>
                                        );
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            <CalendarToolbarActions selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} />
        </div>
    )
}

const CalendarToolbarActions = ({ selectedGroup, setSelectedGroup }) => {
    const { t } = useTranslation("calendar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const groupsState = useAppStore(state => state.groupsState);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [overlapData, setOverlapData] = useState(null);
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);

    const dropdownOptions = useMemo(() => {
        const rawKeys = Object.keys(groupsState || {});
        const displayNames = [...new Set(rawKeys.map(displayGroupName))].sort();
        return [ALL_GROUPS_VALUE, ...displayNames];
    }, [groupsState]);

    return (
        <>
            <div className="cv-tools-container">
                <div className="cv-tools-div">
                    <div className="cv-filter-item">
                        <label className={`cv-groups-filter-label ${isDarkTheme ? 'dark-color' : ''}`}>{t("filters.filterByGroups")}</label>
                        <div
                            className={`cv-dropdown cv-groups-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                            onClick={() => setIsGroupDropdownOpen((prev) => !prev)}
                        >
                            <span className="cv-dropdown-value">{translateGroupDisplay(selectedGroup, t)}</span>
                            <motion.div className="arrow">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    style={{ display: "block" }}
                                >
                                    <path
                                        d="M7 10l5 5 5-5"
                                        fill="none"
                                        stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.div>
                            <AnimatePresence>
                                {isGroupDropdownOpen && (
                                    <motion.div
                                        className={`cv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.14, ease: "easeOut" }}
                                    >
                                        {dropdownOptions.map((name) => (
                                            <div
                                                key={name}
                                                className={`cv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''} ${selectedGroup === name ? 'cv-dropdown-item-selected' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedGroup(name);
                                                    setIsGroupDropdownOpen(false);
                                                }}
                                            >
                                                {translateGroupDisplay(name, t)}
                                            </div>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
                <button
                    className={`cv-new-absence-button ${isDarkTheme ? 'dark-big-btn ' : ''}`}
                    onClick={() => setIsModalOpen(true)}
                >
                    {t("actions.newAbsence")}
                </button>
            </div>
            <AnimatePresence>
                {isModalOpen && (
                    <AbsenceModal
                        onClose={() => setIsModalOpen(false)}
                        onOverlapDetected={setOverlapData}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {overlapData && (
                    <AbsenceOverlapModal
                        overlapData={overlapData}
                        onClose={() => setOverlapData(null)}
                    />
                )}
            </AnimatePresence>
        </>
    )
}

const displayGroupName = (name) => {
    if (name == null || name === '' || name === 'null' || name === 'undefined') return NO_GROUP_LABEL;
    return name;
};

const AbsenceRequestSuccessToast = ({ toastType }) => {
    const { t } = useTranslation("calendar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const setAbsenceRequestSuccessToast = useAppStore(state => state.setAbsenceRequestSuccessToast);

    const dismiss = useCallback(() => {
        setAbsenceRequestSuccessToast(false);
    }, [setAbsenceRequestSuccessToast]);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, [dismiss]);

    const toastKey = toastType === "potvrda" || toastType === "izmena" ? toastType : "podnosenje";
    const title = t(`toast.${toastKey}.title`);
    const message = t(`toast.${toastKey}.message`);

    const content = (
        <motion.div
            className="dv-device-toast-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="na-toast-content">
                    <button
                        type="button"
                        className="na-toast-close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            dismiss();
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="6.8" y1="6.8" x2="21.2" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                            <line x1="21.2" y1="6.8" x2="6.8" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h4 className="na-toast-title">{title}</h4>
                    <p className="na-toast-message-text">
                        {message}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};


const CalendarHeaderButtonsView = () => {
    const isTouchDevice = useLayoutStore(state => state.isTouchDevice)
    const calendarVisibility = useAppStore(state => state.calendarVisibility);
    const setCalendarVisibility = useAppStore(state => state.setCalendarVisibility);
    const [isHovered, setIsHovered] = useState('');

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const activeClass = isDarkTheme ? 'dark-active' : 'active'

    const handleVisibilityChange = (e) => {
        const target = e.target.closest('[data-name]');
        if (!target) return;
        const name = target.getAttribute('data-name');
        setCalendarVisibility(name);
    }

    return (
        <div className="v-header-tools-container calendar-header-buttons-container">
            <div className="v-header-buttons-div" onClick={handleVisibilityChange}>
                {/* Kalendar pogled */}
                {/* <div className="v-header-button-div">
                    <button
                        className={`v-header-icon-button ${calendarVisibility === "calendarMonth" ? activeClass : ""}`}
                        // className="v-header-icon-button"
                        data-name="calendarMonth"
                        onMouseEnter={() => !isTouchDevice && setIsHovered("calendarMonth")}
                        onMouseLeave={() => !isTouchDevice && setIsHovered("")}
                    >
                        <img src="./views/Danas.svg" alt="Kalendar pogled" className={`${isDarkTheme ? 'dark-filter' : ''}`} />
                    </button>
                    <AnimatePresence>
                        {isHovered === "calendarMonth" && (
                            <motion.div
                                className={`v-header-button-tooltip  ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                                initial={{ opacity: 0, y: -14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.19, ease: "easeOut" }}
                            >
                                Kalendar pogled
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div> */}
                {/* Tabelarni pogled */}
                {/* <div className="v-header-button-div">
                    <button
                        className={`v-header-icon-button ${calendarVisibility === "calendarTable" ? activeClass : ""}`}
                        data-name="calendarTable"
                        onMouseEnter={() => !isTouchDevice && setIsHovered("calendarTable")}
                        onMouseLeave={() => !isTouchDevice && setIsHovered("")}
                    >
                        <img
                            src="./views/List.svg"
                            alt="Tabelarni pogled"
                            className={`${isDarkTheme ? 'dark-filter' : ''}`}
                            style={{ transform: "scaleY(1.29)" }}
                        />
                    </button>
                    <AnimatePresence>
                        {isHovered === "calendarTable" && (
                            <motion.div
                                className={`v-header-button-tooltip  ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                                initial={{ opacity: 0, y: -14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.19, ease: "easeOut" }}
                            >
                                Tabelarni pogled
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div> */}
            </div>
        </div>
    );
}

export default CalendarView;
